/**
 * XState Machine for Workshop Registration
 *
 * This machine manages the client-side state for the workshop registration process.
 * The actual business logic and persistence is handled by the DBOS service.
 */
import { assign, createMachine } from 'xstate'

import { Registration, UserProfile, WorkshopPreference } from './workshop-api'

// Context type for the state machine
export interface RegistrationContext {
  // User input
  userEmail: string | null
  userProfile: UserProfile | null
  selectedWorkshops: WorkshopPreference[]

  // Registration data from API
  registration: Registration | null
  registrationId: string | null

  // UI state
  currentStep: number
  errors: string[]
  notifications: string[]

  // Payment
  paymentMethod: 'stripe' | 'xero' | null
  paymentClientSecret: string | null

  // Waitlist
  waitlistPosition: number | null
  spotOfferExpiry: Date | null

  // API communication
  isLoading: boolean
  retryCount: number
}

// Events that can be sent to the machine
export type RegistrationEvent =
  | { type: 'START_REGISTRATION' }
  | { type: 'SUBMIT_BASIC_INFO'; email: string; profile: UserProfile }
  | { type: 'ADD_WORKSHOP_PREFERENCE'; preference: WorkshopPreference }
  | { type: 'REMOVE_WORKSHOP_PREFERENCE'; workshopInstanceId: string }
  | { type: 'SUBMIT_PREFERENCES' }
  | { type: 'EDIT_PREFERENCES' }
  | { type: 'EDIT_INFO' }
  | { type: 'CONFIRM_SELECTION' }
  | { type: 'CHOOSE_PAYMENT_METHOD'; method: 'stripe' | 'xero' }
  | { type: 'PAYMENT_SUCCEEDED'; registrationId: string }
  | { type: 'PAYMENT_FAILED'; error: string }
  | { type: 'JOIN_WAITLIST' }
  | { type: 'ACCEPT_SPOT_OFFER' }
  | { type: 'DECLINE_SPOT_OFFER' }
  | { type: 'SPOT_OFFER_EXPIRED' }
  | { type: 'CANCEL_REGISTRATION'; reason?: string }
  | { type: 'RETRY' }
  | { type: 'API_SUCCESS'; data: any }
  | { type: 'API_ERROR'; error: string }
  | { type: 'VALIDATION_ERROR'; errors: string[] }
  | { type: 'REGISTRATION_UPDATE'; registration: Registration }

// Guards (conditions)
const guards = {
  hasValidBasicInfo: (context: RegistrationContext) => {
    return !!(context.userEmail && context.userEmail.includes('@') && context.userProfile?.name)
  },

  hasValidPreferences: (context: RegistrationContext) => {
    return (
      context.selectedWorkshops.length > 0 &&
      context.selectedWorkshops.every(w => w.preference && w.format)
    )
  },

  canRetry: (context: RegistrationContext) => {
    return context.retryCount < 3
  },

  hasSpotOffer: (context: RegistrationContext) => {
    return !!(context.spotOfferExpiry && context.spotOfferExpiry > new Date())
  },
}

// Actions
const actions = {
  setUserInfo: assign({
    userEmail: (_, event: any) => event.email,
    userProfile: (_, event: any) => event.profile,
    errors: () => [],
  }),

  addWorkshopPreference: assign({
    selectedWorkshops: (context: RegistrationContext, event: any) => {
      const existing = context.selectedWorkshops.find(
        w => w.workshopInstanceId === event.preference.workshopInstanceId
      )
      if (existing) {
        return context.selectedWorkshops.map(w =>
          w.workshopInstanceId === event.preference.workshopInstanceId
            ? { ...w, ...event.preference }
            : w
        )
      }
      return [...context.selectedWorkshops, event.preference]
    },
  }),

  removeWorkshopPreference: assign({
    selectedWorkshops: (context: RegistrationContext, event: any) =>
      context.selectedWorkshops.filter(w => w.workshopInstanceId !== event.workshopInstanceId),
  }),

  setPaymentMethod: assign({
    paymentMethod: (_, event: any) => event.method,
  }),

  setRegistration: assign({
    registration: (_, event: any) => event.registration,
    registrationId: (_, event: any) => event.registration?.id || null,
  }),

  setPaymentClientSecret: assign({
    paymentClientSecret: (_, event: any) => event.clientSecret,
  }),

  setWaitlistPosition: assign({
    waitlistPosition: (_, event: any) => event.position,
  }),

  setSpotOffer: assign({
    spotOfferExpiry: (_, event: any) => new Date(event.expiresAt),
  }),

  addError: assign({
    errors: (context: RegistrationContext, event: any) => [...context.errors, event.error],
  }),

  clearErrors: assign({
    errors: () => [],
  }),

  addNotification: assign({
    notifications: (context: RegistrationContext, event: any) => [
      ...context.notifications,
      typeof event === 'string' ? event : event.message,
    ],
  }),

  setLoading: assign({
    isLoading: (_, event: any) => event.loading ?? true,
  }),

  incrementRetry: assign({
    retryCount: (context: RegistrationContext) => context.retryCount + 1,
  }),

  resetRetry: assign({
    retryCount: () => 0,
  }),

  incrementStep: assign({
    currentStep: (context: RegistrationContext) => context.currentStep + 1,
  }),

  setStep: assign({
    currentStep: (_, event: any) => event.step,
  }),
}

// Services (async operations)
const services = {
  // These will be implemented as callbacks when using the machine
  startRegistration: () => Promise.resolve({}),
  updatePreferences: () => Promise.resolve({}),
  createPaymentIntent: () => Promise.resolve({}),
  joinWaitlist: () => Promise.resolve({}),
  acceptSpotOffer: () => Promise.resolve({}),
  cancelRegistration: () => Promise.resolve({}),
}

// Main state machine
export const workshopRegistrationMachine = createMachine<RegistrationContext, RegistrationEvent>(
  {
    predictableActionArguments: true,
    id: 'workshopRegistration',
    initial: 'landing',
    context: {
      userEmail: null,
      userProfile: null,
      selectedWorkshops: [],
      registration: null,
      registrationId: null,
      currentStep: 0,
      errors: [],
      notifications: [],
      paymentMethod: null,
      paymentClientSecret: null,
      waitlistPosition: null,
      spotOfferExpiry: null,
      isLoading: false,
      retryCount: 0,
    },
    states: {
      // Initial landing state
      landing: {
        entry: ['clearErrors', 'resetRetry'],
        on: {
          START_REGISTRATION: {
            target: 'collectingBasicInfo',
            actions: ['addNotification', 'incrementStep'],
          },
        },
      },

      // Collect basic user information
      collectingBasicInfo: {
        entry: 'setStep',
        on: {
          SUBMIT_BASIC_INFO: [
            {
              target: 'selectingPreferences',
              actions: ['setUserInfo', 'addNotification', 'incrementStep'],
              cond: 'hasValidBasicInfo',
            },
            {
              target: 'collectingBasicInfo',
              actions: ['addError'],
            },
          ],
          VALIDATION_ERROR: {
            actions: ['addError'],
          },
        },
      },

      // Workshop preference selection
      selectingPreferences: {
        entry: 'setStep',
        on: {
          ADD_WORKSHOP_PREFERENCE: {
            actions: 'addWorkshopPreference',
          },
          REMOVE_WORKSHOP_PREFERENCE: {
            actions: 'removeWorkshopPreference',
          },
          SUBMIT_PREFERENCES: {
            target: 'reviewingSelection',
            actions: ['incrementStep'],
            cond: 'hasValidPreferences',
          },
          VALIDATION_ERROR: {
            actions: ['addError'],
          },
        },
      },

      // Review and confirm selection
      reviewingSelection: {
        entry: 'setStep',
        on: {
          CONFIRM_SELECTION: 'submittingRegistration',
          EDIT_PREFERENCES: 'selectingPreferences',
          EDIT_INFO: 'collectingBasicInfo',
        },
      },

      // Submit registration to API
      submittingRegistration: {
        entry: ['setLoading', 'clearErrors'],
        invoke: {
          src: 'startRegistration',
          onDone: {
            target: 'checkingAvailability',
            actions: ['setRegistration', 'resetRetry'],
          },
          onError: {
            target: 'errorState',
            actions: ['addError'],
          },
        },
      },

      // Check workshop availability
      checkingAvailability: {
        entry: 'setLoading',
        always: [
          {
            target: 'paymentRequired',
            cond: context => context.registration?.currentState === 'paymentRequired',
          },
          {
            target: 'waitlistOptions',
            cond: context => context.registration?.currentState === 'waitlistOnly',
          },
          {
            target: 'confirmed',
            cond: context => context.registration?.currentState === 'confirmed',
          },
        ],
      },

      // Payment required
      paymentRequired: {
        entry: ['setStep', 'addNotification'],
        initial: 'selectingPaymentMethod',
        states: {
          selectingPaymentMethod: {
            on: {
              CHOOSE_PAYMENT_METHOD: {
                target: 'processingPayment',
                actions: ['setPaymentMethod'],
              },
              JOIN_WAITLIST: '#workshopRegistration.joiningWaitlist',
            },
          },

          processingPayment: {
            entry: 'setLoading',
            invoke: {
              src: 'createPaymentIntent',
              onDone: {
                target: 'awaitingPayment',
                actions: 'setPaymentClientSecret',
              },
              onError: {
                target: '#workshopRegistration.errorState',
                actions: 'addError',
              },
            },
          },

          awaitingPayment: {
            on: {
              PAYMENT_SUCCEEDED: {
                target: '#workshopRegistration.confirmed',
                actions: ['addNotification', 'resetRetry'],
              },
              PAYMENT_FAILED: {
                target: 'paymentFailed',
                actions: 'addError',
              },
            },
          },

          paymentFailed: {
            on: {
              RETRY: {
                target: 'selectingPaymentMethod',
                actions: 'incrementRetry',
                cond: 'canRetry',
              },
              CHOOSE_PAYMENT_METHOD: {
                target: 'processingPayment',
                actions: 'setPaymentMethod',
              },
              JOIN_WAITLIST: '#workshopRegistration.joiningWaitlist',
            },
          },
        },
      },

      // Waitlist options
      waitlistOptions: {
        entry: ['setStep', 'addNotification'],
        on: {
          JOIN_WAITLIST: 'joiningWaitlist',
          EDIT_PREFERENCES: 'selectingPreferences',
          CANCEL_REGISTRATION: 'cancelled',
        },
      },

      // Joining waitlist
      joiningWaitlist: {
        entry: 'setLoading',
        invoke: {
          src: 'joinWaitlist',
          onDone: {
            target: 'onWaitlist',
            actions: ['setWaitlistPosition', 'addNotification'],
          },
          onError: {
            target: 'errorState',
            actions: 'addError',
          },
        },
      },

      // On waitlist
      onWaitlist: {
        entry: ['setStep', 'addNotification'],
        on: {
          REGISTRATION_UPDATE: [
            {
              target: 'spotOffered',
              actions: ['setRegistration', 'setSpotOffer'],
              cond: (_, event) => event.registration.currentState === 'spotOffered',
            },
            {
              actions: 'setRegistration',
            },
          ],
          EDIT_PREFERENCES: 'selectingPreferences',
          CANCEL_REGISTRATION: 'cancelled',
        },
      },

      // Spot offered from waitlist
      spotOffered: {
        entry: ['setStep', 'addNotification'],
        on: {
          ACCEPT_SPOT_OFFER: {
            target: 'acceptingSpotOffer',
            actions: 'setLoading',
          },
          DECLINE_SPOT_OFFER: 'onWaitlist',
          SPOT_OFFER_EXPIRED: 'onWaitlist',
        },
        after: {
          // Auto-expire after 24 hours (handled by backend, but also tracked here)
          86400000: {
            target: 'onWaitlist',
            actions: 'addNotification',
          },
        },
      },

      // Accepting spot offer
      acceptingSpotOffer: {
        invoke: {
          src: 'acceptSpotOffer',
          onDone: {
            target: 'paymentRequired',
            actions: ['setRegistration', 'addNotification'],
          },
          onError: {
            target: 'spotOffered',
            actions: 'addError',
          },
        },
      },

      // Final confirmed state
      confirmed: {
        type: 'final',
        entry: ['setStep', 'addNotification'],
        on: {
          CANCEL_REGISTRATION: 'cancelling',
        },
      },

      // Cancelling registration
      cancelling: {
        entry: 'setLoading',
        invoke: {
          src: 'cancelRegistration',
          onDone: 'cancelled',
          onError: {
            target: 'confirmed',
            actions: 'addError',
          },
        },
      },

      // Cancelled state
      cancelled: {
        type: 'final',
        entry: 'addNotification',
      },

      // Error state with retry capability
      errorState: {
        entry: 'addNotification',
        on: {
          RETRY: {
            target: 'submittingRegistration',
            actions: 'incrementRetry',
            cond: 'canRetry',
          },
          EDIT_PREFERENCES: 'selectingPreferences',
          EDIT_INFO: 'collectingBasicInfo',
          CANCEL_REGISTRATION: 'cancelled',
        },
      },
    },

    // Handle real-time updates from the API
    on: {
      REGISTRATION_UPDATE: {
        actions: 'setRegistration',
      },
    },
  },
  {
    guards,
    // @ts-expect-error - XState actions typing is very strict; the separate actions object is intentionally dynamic
    actions,
    services,
  }
)

// Utility function to get human-readable state names
export const getStateDisplayName = (state: string): string => {
  const stateNames: Record<string, string> = {
    landing: 'Welcome',
    collectingBasicInfo: 'Your Information',
    selectingPreferences: 'Select Workshops',
    reviewingSelection: 'Review Selection',
    submittingRegistration: 'Processing...',
    checkingAvailability: 'Checking Availability...',
    paymentRequired: 'Payment',
    waitlistOptions: 'Waitlist Options',
    joiningWaitlist: 'Joining Waitlist...',
    onWaitlist: 'On Waitlist',
    spotOffered: 'Spot Available!',
    acceptingSpotOffer: 'Accepting Spot...',
    confirmed: 'Confirmed!',
    cancelling: 'Cancelling...',
    cancelled: 'Cancelled',
    errorState: 'Error',
  }

  return stateNames[state] || state
}

// Helper to get step progress
export const getStepProgress = (currentStep: number): number => {
  const totalSteps = 6 // Adjust based on your flow
  return Math.min((currentStep / totalSteps) * 100, 100)
}

export default workshopRegistrationMachine
